import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  UsersFilters,
  UserCreateInput,
  UserUpdateInput,
} from '@/modules/users/types/user'
import * as usersApi from '@/modules/users/api/usersApi'

/**
 * Query keys for user management
 */
export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (filters?: UsersFilters) => [...usersKeys.lists(), { filters }] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: number) => [...usersKeys.details(), id] as const,
  stats: () => [...usersKeys.all, 'stats'] as const,
  groups: (userId: number) => [...usersKeys.all, 'groups', userId] as const,
}

/**
 * Hook to get paginated users list
 */
export function useUsers(filters?: UsersFilters) {
  return useQuery({
    queryKey: usersKeys.list(filters),
    queryFn: () => usersApi.getUsers(filters),
  })
}

/**
 * Hook to get user statistics
 */
export function useUserStats() {
  return useQuery({
    queryKey: usersKeys.stats(),
    queryFn: () => usersApi.getUserStats(),
  })
}

/**
 * Hook to get single user
 */
export function useUser(userId: number) {
  return useQuery({
    queryKey: usersKeys.detail(userId),
    queryFn: () => usersApi.getUser(userId),
    enabled: !!userId,
  })
}

/**
 * Hook to get user groups
 */
export function useUserGroups(userId: number) {
  return useQuery({
    queryKey: usersKeys.groups(userId),
    queryFn: () => usersApi.getUserGroups(userId),
    enabled: !!userId,
  })
}

/**
 * Hook to create user
 */
export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UserCreateInput) => usersApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: usersKeys.stats() })
      toast.success('Usuario creado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear usuario')
    },
  })
}

/**
 * Hook to update user (PATCH - partial update)
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: Partial<UserUpdateInput> }) =>
      usersApi.partialUpdateUser(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.userId) })
      queryClient.invalidateQueries({ queryKey: usersKeys.stats() })
      toast.success('Usuario actualizado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar usuario')
    },
  })
}

/**
 * Hook to delete (deactivate) user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => usersApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: usersKeys.stats() })
      toast.success('Usuario desactivado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al desactivar usuario')
    },
  })
}

/**
 * Hook to activate user
 */
export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => usersApi.activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() })
      queryClient.invalidateQueries({ queryKey: usersKeys.stats() })
      toast.success('Usuario activado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al activar usuario')
    },
  })
}

/**
 * Hook to add user to group
 */
export function useAddUserToGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, groupId }: { userId: number; groupId: number }) =>
      usersApi.addUserToGroup(userId, groupId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.groups(variables.userId) })
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.userId) })
      toast.success('Usuario agregado al grupo')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al agregar usuario al grupo')
    },
  })
}

/**
 * Hook to remove user from group
 */
export function useRemoveUserFromGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, groupId }: { userId: number; groupId: number }) =>
      usersApi.removeUserFromGroup(userId, groupId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.groups(variables.userId) })
      queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.userId) })
      toast.success('Usuario removido del grupo')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al remover usuario del grupo')
    },
  })
}
